// Plain-language guide for material uses (interchangeable shop parts).
// Hang at Inventory, Models → Uses, and BOM authoring — not only in the
// power-user Uses editor.
import type { ReactNode } from "react";

type Variant = "inventory" | "uses-empty" | "uses-create" | "bom";

const EXAMPLE =
  "Example: a door needs a doorknob. You stock Doorknob Co. and Doorknob Bob. Create a use called “Doorknob,” add both SKUs as members, then put “Doorknob” on the BOM instead of pinning one vendor.";

export function ManufacturingMaterialUseExplainer({
  variant,
  onDismiss,
  action,
}: {
  variant: Variant;
  onDismiss?: () => void;
  action?: ReactNode;
}) {
  const title =
    variant === "bom"
      ? "Prefer a “Use” when several SKUs can fill the same job"
      : variant === "uses-create"
        ? "Name the job, then add interchangeable SKUs"
        : "Interchangeable parts (material uses)";

  const body =
    variant === "bom"
      ? "Pick Use when the line is a functional need (doorknob, floor sheet), not one fixed SKU. At build time the plant picks a preferred or in-stock member."
      : variant === "uses-create"
        ? "Name what the part does on the shop floor. After you save, add every plant SKU that can fill that job (preference + qty per use)."
        : "When two materials can fill the same job, group them as a use. BOMs reference the job; inventory still issues a concrete SKU.";

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/80 px-3 py-3 text-sm text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-100">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <p className="font-semibold">{title}</p>
          <p className="text-sky-900/90 dark:text-sky-100/90">{body}</p>
          {variant !== "bom" ? (
            <p className="text-xs leading-relaxed text-sky-800/90 dark:text-sky-200/80">{EXAMPLE}</p>
          ) : null}
          {variant === "inventory" ? (
            <ol className="list-decimal space-y-0.5 pl-4 text-xs text-sky-900/90 dark:text-sky-100/85">
              <li>Select 2+ materials that can substitute for each other.</li>
              <li>Choose “Make interchangeable…” and name the job.</li>
              <li>On a model BOM, add a Use line for that job instead of one SKU.</li>
            </ol>
          ) : null}
          {variant === "uses-empty" ? (
            <ol className="list-decimal space-y-0.5 pl-4 text-xs text-sky-900/90 dark:text-sky-100/85">
              <li>Create a use (the job name).</li>
              <li>Add member materials for each plant that stocks alternatives.</li>
              <li>On Models, set a BOM line to Use and pick this job.</li>
            </ol>
          ) : null}
          {action ? <div className="pt-1">{action}</div> : null}
        </div>
        {onDismiss ? (
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-sky-800 underline-offset-2 hover:underline dark:text-sky-200"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
}
